import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useAppDispatch } from '../hooks/reduxHooks';
import {
  fetchRecentTransactions,
  fetchWalletBalance,
  verifyKhaltiPayment,
} from '../store/slices/walletSlice';
import { walletService } from '../services/api';
import { r } from '../theme/riderTheme';
import {
  ButtonChevron,
  GhostButton,
  PageHeader,
  PageHeaderAccent,
  PageHeaderEyebrow,
  PageHeaderText,
  PageHeaderTitle,
  PrimaryButton,
} from '../theme/surfacePrimitives';

type ReturnState = 'loading' | 'success' | 'pending' | 'failed';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// Mobile-first card that stays card-shaped on desktop. 520px tracks the rest
// of the rider portal's modal/sheet feel — large enough to read comfortably
// on a laptop, narrow enough that the layout doesn't sprawl.
const ResultCard = styled.section`
  position: relative;
  max-width: 520px;
  margin: 0 auto;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 20px;
  padding: 28px 22px 22px;
  text-align: center;
  overflow: hidden;
  animation: ${fadeIn} 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow: 0 24px 60px -28px rgba(18, 18, 21, 0.18);
`;

const StateRail = styled.div<{ $variant: ReturnState }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${({ $variant, theme }) => {
    const t = r(theme);
    switch ($variant) {
      case 'success':
        return `linear-gradient(90deg, transparent 0%, ${t.accent.limeDeep} 18%, ${t.accent.lime} 50%, ${t.accent.limeDeep} 82%, transparent 100%)`;
      case 'pending':
        return `linear-gradient(90deg, transparent 0%, #d99304 22%, #f5a524 50%, #d99304 78%, transparent 100%)`;
      case 'failed':
        return `linear-gradient(90deg, transparent 0%, ${t.accent.alert} 22%, #ff525a 50%, ${t.accent.alert} 78%, transparent 100%)`;
      case 'loading':
      default:
        return `linear-gradient(90deg, transparent 0%, ${t.surface.edge} 18%, ${t.surface.edgeStrong} 50%, ${t.surface.edge} 82%, transparent 100%)`;
    }
  }};
  opacity: 0.85;
`;

// Force its own row by being a flex parent rather than inline-flex — at
// desktop widths inline-flex would let it sit next to siblings.
const IndicatorRing = styled.div<{ $variant: ReturnState }>`
  margin: 6px auto 18px;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant, theme }) => {
    const t = r(theme);
    switch ($variant) {
      case 'success':
        return 'rgba(0, 185, 70, 0.10)';
      case 'pending':
        return 'rgba(245, 165, 36, 0.10)';
      case 'failed':
        return 'rgba(255, 25, 35, 0.08)';
      case 'loading':
      default:
        return t.surface.inputIdle;
    }
  }};
  border: 1.5px solid
    ${({ $variant, theme }) => {
      const t = r(theme);
      switch ($variant) {
        case 'success':
          return t.accent.ok;
        case 'pending':
          return 'rgba(245, 165, 36, 0.55)';
        case 'failed':
          return 'rgba(255, 25, 35, 0.5)';
        case 'loading':
        default:
          return t.surface.edge;
      }
    }};
`;

const RingSpinner = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 720ms linear infinite;
`;

const StatusGlyph = styled.span<{ $variant: 'success' | 'pending' | 'failed' }>`
  font-family: 'Geist', sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $variant, theme }) => {
    if ($variant === 'success') return r(theme).accent.ok;
    if ($variant === 'pending') return '#d99304';
    return r(theme).accent.alert;
  }};
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-family: 'Geist', sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => r(theme).text.primary};
`;

const TitleAccent = styled.span<{ $variant: ReturnState }>`
  color: ${({ $variant, theme }) => {
    const t = r(theme);
    if ($variant === 'success') return t.accent.ok;
    if ($variant === 'pending') return '#d99304';
    if ($variant === 'failed') return t.accent.alert;
    return t.text.primary;
  }};
`;

const Body = styled.p`
  margin: 0 auto 22px;
  max-width: 360px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.muted};
`;

// Digital-receipt block. Each row is a label/value pair; rows divided by hairlines.
const Receipt = styled.div`
  margin: 0 0 22px;
  padding: 4px 16px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 14px;
  text-align: left;
  font-feature-settings: 'tnum';
`;

const ReceiptRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => r(theme).surface.edge};
  }
`;

const ReceiptTerm = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const ReceiptValue = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => r(theme).text.primary};
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
`;

const ReceiptAmount = styled(ReceiptValue)`
  color: ${({ theme }) => r(theme).accent.ok};
  font-size: 16px;
`;

const ReceiptMono = styled(ReceiptValue)`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
`;

// Standalone ref line for non-success states (no full receipt to anchor to).
const RefLine = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: 0 0 22px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
  gap: 10px;
`;

const ErrorBlock = styled.div`
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 25, 35, 0.05);
  border: 1px solid rgba(255, 25, 35, 0.24);
  color: ${({ theme }) => r(theme).accent.alert};
  font-family: 'Geist', sans-serif;
  font-size: 12.5px;
  line-height: 1.45;
  text-align: left;
  overflow-wrap: anywhere;
`;

const RedirectNote = styled.div<{ $hidden?: boolean }>`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-top: 14px;
  ${({ $hidden }) =>
    $hidden &&
    css`
      visibility: hidden;
    `}
`;

const titleByState: Record<ReturnState, { lead: string; accent: string }> = {
  loading: { lead: 'Verifying', accent: 'payment' },
  success: { lead: 'Wallet', accent: 'loaded' },
  pending: { lead: 'Payment', accent: 'pending' },
  failed: { lead: 'Payment', accent: 'not completed' },
};

const eyebrowByState: Record<ReturnState, string> = {
  loading: 'Wallet · Top-up · Verifying',
  success: 'Wallet · Top-up · Confirmed',
  pending: 'Wallet · Top-up · Pending',
  failed: 'Wallet · Top-up · Failed',
};

const KhaltiReturnPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ReturnState>('loading');
  const [message, setMessage] = useState('Verifying your Khalti payment.');
  const [amountNpr, setAmountNpr] = useState<number | null>(null);
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const pidx = searchParams.get('pidx') || '';
  const transactionId =
    searchParams.get('transaction_id') ||
    searchParams.get('txnId') ||
    searchParams.get('tidx') ||
    undefined;
  const status = searchParams.get('status') || undefined;

  useEffect(() => {
    let cancelled = false;

    const verifyPayment = async () => {
      if (!pidx) {
        setState('failed');
        setMessage('Khalti did not return a payment reference.');
        return;
      }

      try {
        const result = await dispatch(
          verifyKhaltiPayment({ pidx, transactionId, status }),
        ).unwrap();
        if (cancelled) return;

        setMessage(result.message);
        if (typeof result.new_balance === 'number') {
          setNewBalance(result.new_balance);
        }
        if (result.transaction && typeof result.transaction.amount_npr === 'number') {
          setAmountNpr(result.transaction.amount_npr);
        }

        if (result.success) {
          setState('success');
          dispatch(fetchWalletBalance());
          dispatch(fetchRecentTransactions(5));
        } else if (result.status === 'Pending') {
          setState('pending');
        } else {
          setState('failed');
        }
      } catch (error: unknown) {
        if (cancelled) return;
        setState('failed');
        const fallback =
          error instanceof Error ? error.message : 'Payment verification failed.';
        setMessage(fallback);
      }
    };

    verifyPayment();

    return () => {
      cancelled = true;
    };
  }, [dispatch, pidx, status, transactionId]);

  const shortRef = pidx ? pidx.slice(-12) : '';
  const t = titleByState[state];

  const receiptVisible =
    state === 'success' && (amountNpr != null || newBalance != null || shortRef);

  return (
    <>
      <PageHeader>
        <PageHeaderText>
          <PageHeaderEyebrow>{eyebrowByState[state]}</PageHeaderEyebrow>
          <PageHeaderTitle>
            Khalti <PageHeaderAccent>return</PageHeaderAccent>
          </PageHeaderTitle>
        </PageHeaderText>
      </PageHeader>

      <ResultCard aria-live="polite">
        <StateRail $variant={state} aria-hidden />

        <IndicatorRing $variant={state} aria-hidden>
          {state === 'loading' && <RingSpinner />}
          {state === 'success' && <StatusGlyph $variant="success">✓</StatusGlyph>}
          {state === 'pending' && <StatusGlyph $variant="pending">⌛</StatusGlyph>}
          {state === 'failed' && <StatusGlyph $variant="failed">×</StatusGlyph>}
        </IndicatorRing>

        <Title>
          {t.lead} <TitleAccent $variant={state}>{t.accent}</TitleAccent>
        </Title>
        {/* Failed state surfaces `message` via ErrorBlock below so the body
            copy doesn't repeat the same error text. */}
        {state !== 'failed' && <Body>{message}</Body>}

        {receiptVisible && (
          <Receipt>
            {amountNpr != null && (
              <ReceiptRow>
                <ReceiptTerm>Loaded</ReceiptTerm>
                <ReceiptAmount>
                  +{walletService.formatCurrency(amountNpr)}
                </ReceiptAmount>
              </ReceiptRow>
            )}
            {newBalance != null && (
              <ReceiptRow>
                <ReceiptTerm>New balance</ReceiptTerm>
                <ReceiptValue>
                  {walletService.formatCurrency(newBalance)}
                </ReceiptValue>
              </ReceiptRow>
            )}
            {shortRef && (
              <ReceiptRow>
                <ReceiptTerm>Reference</ReceiptTerm>
                <ReceiptMono>{shortRef}</ReceiptMono>
              </ReceiptRow>
            )}
          </Receipt>
        )}

        {state === 'failed' && message && <ErrorBlock>{message}</ErrorBlock>}

        {state !== 'success' && shortRef && (
          <RefLine aria-label={`Khalti reference ending ${shortRef}`}>
            <span>Ref · {shortRef}</span>
          </RefLine>
        )}

        <ButtonRow>
          <GhostButton onClick={() => navigate('/dashboard', { replace: true })}>
            <span>Back home</span>
          </GhostButton>
          <PrimaryButton onClick={() => navigate('/wallet', { replace: true })}>
            <span>{state === 'success' ? 'View wallet' : 'Open wallet'}</span>
            <ButtonChevron aria-hidden>→</ButtonChevron>
          </PrimaryButton>
        </ButtonRow>

        <RedirectNote $hidden={state !== 'loading'}>
          {state === 'loading' ? 'Hold tight — confirming with Khalti…' : ''}
        </RedirectNote>
      </ResultCard>
    </>
  );
};

export default KhaltiReturnPage;
